import OlMap from 'ol/Map';

import { AlignmentsLayer } from '@openlayers/alignments/AlignmentsLayer';
import { AlignmentFeatureFactory } from '@openlayers/alignments/AlignmentFeatureFactory';
import { GtPointFeatureFactory } from '@openlayers/points/GtPointFeatureFactory';
import { TekPointFeatureFactory } from '@openlayers/points/TekPointFeatureFactory';
import { PipesLayer } from '@openlayers/pipes/PipesLayer';
import { PipeFeatureFactory } from '@openlayers/pipes/PipeFeatureFactory';
import { StringlinesLayer } from '@openlayers/stringlines/StringlinesLayer';
import { StringlineFeatureFactory } from '@openlayers/stringlines/StringlineFeatureFactory';
import { ModelsLayer } from '@openlayers/models/ModelsLayer';
import { ModelOutlineFeatureFactory } from '@openlayers/models/ModelOutlineFeatureFactory';
import { VehiclesLayer } from '@openlayers/vehicles/VehiclesLayer';
import { VehiclesFeatureFactory } from '@openlayers/vehicles/VehiclesFeatureFactory';
import { LogpointsLayer } from '@openlayers/logpoints/LogpointsLayer';
import { GtPointsLayer } from '@openlayers/points/GtPointsLayer';
import { TekPointsLayer } from '@openlayers/points/TekPointsLayer';
import { LogpointFeatureFactory } from '@openlayers/logpoints/LogpointFeatureFactory';
import { DocumentsLayer } from '@openlayers/documents/DocumentsLayer';
import { DocumentFeatureFactory } from '@openlayers/documents/DocumentFeatureFactory';
import { ImagesLayer } from '@openlayers/images/ImagesLayer';
import { ImageFeatureFactory } from '@openlayers/images/ImageFeatureFactory';

import { LogpointDesktopMouseInteraction } from './LogpointDesktopMouseInteraction';
import { GtPointDesktopMouseInteraction } from './GtPointDesktopMouseInteraction';
import { ModelOutlineDesktopMouseInteraction } from '@pages/map/map/ModelOutlineDesktopMouseInteraction';
import { PipeDesktopMouseInteraction } from '@pages/map/map/PipeDesktopMouseInteraction';
import { StringlineDesktopMouseInteraction } from '@pages/map/map/StringlineDesktopMouseInteraction';
import { AlignmentDesktopMouseInteraction } from '@pages/map/map/AlignmentDesktopMouseInteraction';
import { VehicleDesktopMouseInteraction } from '@pages/map/map/VehicleDesktopMouseInteraction';
import { DocumentDesktopMouseInteraction } from '@pages/map/map/DocumentDesktopMouseInteraction';
import { ImageDesktopMouseInteraction } from '@pages/map/map/ImageDesktopMouseInteraction';
import { mapRootStore, mapStateFacade, mapUI } from '@pages/map/map';
import { SelectedFeatureHighlightFactory, SelectedFeatureHighlightLayer } from '@openlayers/SelectedFeatureHighlightLayer';
import { PageStateLayer } from '@shared/user-state/PageStateLayer';
import { PageStateDesktopMouseInteraction } from '@pages/map/map/PageStateDesktopMouseInteraction';
import { MarineLayer } from '@shared/marine/MarineLayer';
import { StructureLayer } from '@shared/openlayers/models/StructureLayer';
import { TekPointDesktopMouseInteraction } from './TekPointDesktopMouseInteraction';

export function makeLayers(map: OlMap) {
    const pointsLayer = new GtPointsLayer({
        featureType: 'model_point',
        makeFeature: GtPointFeatureFactory.makeFeature,
        mouseInteraction: new GtPointDesktopMouseInteraction(map),
        lazyAddToMap: true,
        zIndex: 31,
        map,
    });

    const tekPointsLayer = new TekPointsLayer({
        featureType: 'tek_point',
        makeFeature: TekPointFeatureFactory.makeFeature,
        mouseInteraction: new TekPointDesktopMouseInteraction(map),
        lazyAddToMap: true,
        zIndex: 32,
        map,
    });

    const logpointsLayer = new LogpointsLayer({
        makeFeature: LogpointFeatureFactory.makeFeature,
        mouseInteraction: new LogpointDesktopMouseInteraction(map),
        layerBuilderFn: () => null,
        map,
    });

    return {
        highlighter: new SelectedFeatureHighlightLayer({
            makeFeature: SelectedFeatureHighlightFactory.makeFeatures,
            styleFunction: SelectedFeatureHighlightFactory.styleFunction,
        }).addToMap(map, 1),

        models: new ModelsLayer(
            {
                makeFeature: ModelOutlineFeatureFactory.makeFeatures,
                styleFunction: ModelOutlineFeatureFactory.styleFunction,
                mouseInteraction: new ModelOutlineDesktopMouseInteraction(map),
            },
            pointsLayer,
            tekPointsLayer
        ).addToMap(map, 10),

        pipes: new PipesLayer({
            featureType: 'pipe',
            makeFeature: PipeFeatureFactory.makeFeatures,
            styleFunction: PipeFeatureFactory.styleFunction,
            mouseInteraction: new PipeDesktopMouseInteraction(map),
        }).addToMap(map, 20),

        stringlines: new StringlinesLayer(
            {
                featureType: 'stringline',
                makeFeature: StringlineFeatureFactory.makeFeature,
                styleFunction: StringlineFeatureFactory.styleFunction,
                mouseInteraction: new StringlineDesktopMouseInteraction(map),
            },
            pointsLayer
        ).addToMap(map, 30),

        points: pointsLayer,

        tekPoints: tekPointsLayer,

        alignments: new AlignmentsLayer(
            {
                featureType: 'alignment',
                makeFeature: AlignmentFeatureFactory.makeFeature,
                styleFunction: AlignmentFeatureFactory.styleFunction,
                mouseInteraction: new AlignmentDesktopMouseInteraction(map),
            },
            mapRootStore.alignmentStore
        ).addToMap(map, 40),

        vehicles: new VehiclesLayer(
            {
                makeFeature: VehiclesFeatureFactory.makeFeature,
                styleFunction: VehiclesFeatureFactory.styleFunction,
                mouseInteraction: new VehicleDesktopMouseInteraction(map),
            },
            mapRootStore.vehiclesStore
        ).addToMap(map, 50),

        logpoints: logpointsLayer,

        documents: new DocumentsLayer(
            {
                makeFeature: DocumentFeatureFactory.makeFeature,
                mouseInteraction: new DocumentDesktopMouseInteraction(map),
            },
            mapRootStore.documentsStore,
            id => mapUI.mapPanels.panels.documents.getTreeNodeById(id)
        ).addToMap(map, 65),

        images: new ImagesLayer(
            {
                makeFeature: ImageFeatureFactory.makeFeature,
                mouseInteraction: new ImageDesktopMouseInteraction(map),
            },
            mapRootStore.imagesStore,
            id => mapUI.mapPanels.panels.images.getTreeNodeById(id)
        ).addToMap(map, 70),

        pageState: new PageStateLayer(
            {
                mouseInteraction: new PageStateDesktopMouseInteraction(map),
            },
            mapStateFacade.store
        ).addToMap(map, 71),

        structures: new StructureLayer({}).addToMap(map, 10),

        marine: new MarineLayer(mapRootStore.marineStore, map),
    };
}
